import { ArrowLeft, BookMarked, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { ApiError, getDocument, rereadSource } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import type { Document, Extraction } from "@/api/client";
import { READ_KINDS, kindLabel } from "@/screens/knowledge/kinds";
import { useExtraction } from "@/screens/knowledge/useExtraction";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { EmptyState } from "@/ui/EmptyState";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Skeleton, Spinner } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

const NOTE_LABEL: Record<Extraction["notes"][number]["kind"], string> = {
  condition: "Condition",
  corner: "Corner",
  job_code: "Job code",
  check: "To check",
  other: "Note",
  legend: "Legend",
};

/** A code legend's reading: its words, offered to the code standard (K3). */
function LegendRead({ extraction }: { extraction: Extraction }) {
  return (
    <>
      {extraction.warnings.map((w) => (
        <Notice key={w} tone="warn" className="mt-4">
          {w}
        </Notice>
      ))}
      <p className="mt-6 max-w-2xl text-sm text-ink-2">
        Its text boxes suggest what each part of your codes means. Review and confirm them on the{" "}
        <Link to="/knowledge/code-standard">Code standard</Link> tab.
      </p>
      <Table aria-label="Legend text" className="mt-4">
        <thead>
          <tr>
            <Th className="w-20">Page</Th>
            <Th>Text</Th>
          </tr>
        </thead>
        <tbody>
          {extraction.notes.map((n, i) => (
            <tr key={`${n.page_index}-${i}`}>
              <Td className="tabular text-ink-2">{n.page_index + 1}</Td>
              <Td className="text-ink">{n.text}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <p className="text-xs text-muted">{label}</p>
      <p className="tabular mt-1 text-xl font-semibold text-ink">{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

function pct(part: number, whole: number): string {
  return whole ? `${Math.round((part / whole) * 100)}%` : "—";
}

/**
 * One past take-off: what was read from its markup, and what it taught the library
 * (ADR-0014). Everything here stays a proposal until an estimator accepts it on a set.
 */
export function SourceScreen() {
  const { id = "" } = useParams();
  const [doc, setDoc] = useState<Document | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const { extraction, error, reload } = useExtraction(id);
  const canManage = useCan("knowledge:manage");

  useEffect(() => {
    let cancelled = false;
    getDocument(id)
      .then((got) => {
        if (!cancelled) setDoc(got);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setDocError(err instanceof ApiError ? err.detail : "Could not reach the server.");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const back = (
    <Link
      to="/knowledge/sources"
      className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Sources
    </Link>
  );

  if (docError) {
    return (
      <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
        {back}
        <Notice tone="danger" className="mt-4">
          {docError}
        </Notice>
      </main>
    );
  }

  const reading =
    extraction === null || extraction.status === "none" || extraction.status === "running";
  const s = extraction?.stats ?? {};

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      {back}
      <div className="mt-3">
        <PageHeader
          title={doc?.filename ?? "…"}
          description={doc ? kindLabel(doc.kind) : undefined}
          actions={
            canManage && doc && READ_KINDS.has(doc.kind) ? (
              <Button disabled={reading} onClick={() => void rereadSource(id).then(reload)}>
                <RefreshCw aria-hidden="true" />
                Read again
              </Button>
            ) : null
          }
        />
      </div>

      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}

      {doc && !READ_KINDS.has(doc.kind) ? (
        <div className="mt-6">
          <EmptyState icon={<BookMarked />} title="Kept for reference">
            This kind of source is stored with your knowledge. Reading it comes in a later step;
            past take-offs and code legends are read today.
          </EmptyState>
        </div>
      ) : reading ? (
        <div className="mt-8 flex items-center gap-3 text-sm text-ink-2" role="status">
          <Spinner className="size-4" />
          {doc?.kind === "code_standard"
            ? "Reading the legend…"
            : "Reading the markup and measuring the panels on its sheets…"}
        </div>
      ) : extraction?.status === "failed" ? (
        <Notice tone="danger" className="mt-6">
          It could not be read: {extraction.error}
        </Notice>
      ) : extraction && doc?.kind === "code_standard" ? (
        <LegendRead extraction={extraction} />
      ) : extraction ? (
        <>
          {extraction.warnings.map((w) => (
            <Notice key={w} tone="warn" className="mt-4">
              {w}
            </Notice>
          ))}

          <section
            aria-label="What was read"
            className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4"
          >
            <Stat
              label="Units the estimator marked"
              value={(s.boxes ?? 0).toLocaleString()}
              hint={`on ${s.marked_pages ?? 0} of ${s.pages ?? 0} sheets`}
            />
            <Stat
              label="With a code"
              value={(s.coded ?? 0).toLocaleString()}
              hint={`${(s.unresolved ?? 0).toLocaleString()} left to settle`}
            />
            <Stat
              label="Matched to measured panels"
              value={pct(s.matched ?? 0, s.coded ?? 0)}
              hint={`${(s.matched ?? 0).toLocaleString()} of ${(s.coded ?? 0).toLocaleString()}`}
            />
            <Stat
              label="Types learned"
              value={extraction.taught.length}
              hint={`${extraction.codes.length} codes`}
            />
          </section>

          <section aria-label="Types learned" className="mt-8">
            <h2 className="text-base font-semibold text-ink">Types learned</h2>
            <p className="mt-1 text-sm text-ink-2">
              Each panel make-up the system measured, and what the estimator called it. A new set
              with the same make-up is proposed the library&apos;s name.
            </p>
            {extraction.taught.length === 0 ? (
              <p className="mt-3 text-sm text-muted">
                No panels could be measured on these sheets, so nothing was learned.
              </p>
            ) : (
              <Table aria-label="Types learned" className="mt-3">
                <thead>
                  <tr>
                    <Th>Make-up</Th>
                    <Th>Estimator&apos;s codes</Th>
                    <Th className="text-right">Panels</Th>
                    <Th>Named in the library</Th>
                  </tr>
                </thead>
                <tbody>
                  {extraction.taught.map((t) => (
                    <tr key={t.config_key}>
                      <Td className="text-ink">
                        <span className="block">{t.summary}</span>
                        <span className="block font-mono text-xs text-muted">{t.config_key}</span>
                      </Td>
                      <Td className="text-ink-2">
                        {t.codes.slice(0, 4).map((c, i) => (
                          <span key={c.code} className="mr-2 inline-block whitespace-nowrap">
                            <span className={i === 0 ? "font-medium text-ink" : ""}>{c.code}</span>{" "}
                            <span className="tabular text-xs text-muted">
                              {pct(c.count, t.panels)}
                            </span>
                          </span>
                        ))}
                        {t.codes.length > 4 ? (
                          <span className="text-xs text-muted">+{t.codes.length - 4} more</span>
                        ) : null}
                      </Td>
                      <Td className="tabular text-right text-ink-2">{t.panels}</Td>
                      <Td>
                        {t.named ? (
                          <Badge tone={t.named_by === "estimator" ? "ok" : "accent"}>
                            {t.named}
                            {t.named_by === "estimator" ? " · named by an estimator" : ""}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </section>

          {extraction.notes.length > 0 ? (
            <section aria-label="Notes" className="mt-8">
              <h2 className="text-base font-semibold text-ink">The estimator&apos;s notes</h2>
              <Table aria-label="Notes" className="mt-3">
                <thead>
                  <tr>
                    <Th>Sheet</Th>
                    <Th>Kind</Th>
                    <Th>Note</Th>
                  </tr>
                </thead>
                <tbody>
                  {extraction.notes.map((n, i) => (
                    <tr key={i}>
                      <Td className="whitespace-nowrap text-ink-2">
                        {n.sheet_number ?? `Page ${n.page_index + 1}`}
                      </Td>
                      <Td className="whitespace-nowrap">
                        <Badge tone={n.kind === "check" ? "warn" : "neutral"}>
                          {NOTE_LABEL[n.kind]}
                        </Badge>
                      </Td>
                      <Td className="text-ink">{n.text}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          ) : null}
        </>
      ) : (
        <div className="mt-6 space-y-2">
          <Skeleton className="h-20 w-full" />
        </div>
      )}
    </main>
  );
}
